import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQueryClient } from '@tanstack/react-query'

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { toast } from 'sonner'

import showErrorToast from '@/common/utils/showErrorToast'
import { RtpPresetPrecedenceTooltip } from '@/common/components/RtpPresetPrecedenceTooltip'
import useMutationUpdatePlayerTag from '../../../../api/hooks/useMutationUpdatePlayerTag'
import useQueryGetPlayerTagById from '../../../../api/hooks/useQueryGetPlayerTagById'
import { TagsQueryKeys } from '../../../../constants'
import { PlayerQueryKeys } from '@/modules/manage-player/keys'
import useQueryGetPresetId from '@/modules/promotion/api/hooks/useQueryGetPresetId'
import { PresetPriorityHint } from '@/common/components/PresetPriorityHint'

const updatePlayerTagSchema = z.object({
	name: z.string().min(1, 'กรุณากรอกชื่อแท็ก'),
	color: z
		.string()
		.regex(/^#[0-9A-F]{6}$/i, 'กรุณากรอกรหัสสีที่ถูกต้อง (เช่น #FFD700)'),
	autoTagCondition: z.enum(['none', 'minDepositAmount', 'depositGtWithdraw']),
	minDepositAmount: z.string().optional(),
	depositGtWithdrawAmount: z.string().optional(),
	presetId: z.string().optional(),
	presetNumber: z.string().optional(),
})

type UpdatePlayerTagFormValues = z.infer<typeof updatePlayerTagSchema>

interface EditPlayerTagModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	tagId: string | null
}

export default function EditPlayerTagModal({
	open,
	onOpenChange,
	tagId,
}: EditPlayerTagModalProps) {
	const queryClient = useQueryClient()
	const [isLoading, setIsLoading] = useState(false)

	// Fetch tag data by ID
	const { data: tag, isLoading: isLoadingTag } = useQueryGetPlayerTagById(tagId)

	// Fetch presets for dropdown
	const { data: presets } = useQueryGetPresetId()

	const form = useForm<UpdatePlayerTagFormValues>({
		resolver: zodResolver(updatePlayerTagSchema),
		defaultValues: {
			name: '',
			color: '#000000',
			autoTagCondition: 'none',
			minDepositAmount: '',
			depositGtWithdrawAmount: '',
			presetId: '',
			presetNumber: '',
		},
	})

	const autoTagCondition = form.watch('autoTagCondition')

	// Reset form values when tag data is loaded
	useEffect(() => {
		if (tag) {
			// Determine the auto tag condition based on existing values
			let condition: 'none' | 'minDepositAmount' | 'depositGtWithdraw' = 'none'
			if (tag.depositGtWithdraw) {
				condition = 'depositGtWithdraw'
			} else if (tag.minDepositAmount) {
				condition = 'minDepositAmount'
			}

			form.reset({
				name: tag.name,
				color: tag.color,
				autoTagCondition: condition,
				minDepositAmount: tag.minDepositAmount?.toString() || '',
				depositGtWithdrawAmount: tag.depositGtWithdraw?.toString() || '',
				presetId: (tag.presetId as unknown as string | null) ?? '',
				presetNumber: tag.presetNumber?.toString() ?? '',
			})
		}
	}, [tag, form])

	const { mutateAsync: updateTag } = useMutationUpdatePlayerTag()

	const onSubmit = async (values: UpdatePlayerTagFormValues) => {
		if (!tag) return

		try {
			setIsLoading(true)

			const payload = {
				name: values.name,
				color: values.color,
				minDepositAmount:
					values.autoTagCondition === 'minDepositAmount' &&
					values.minDepositAmount
						? parseFloat(values.minDepositAmount)
						: null,
				depositGtWithdraw:
					values.autoTagCondition === 'depositGtWithdraw' &&
					values.depositGtWithdrawAmount
						? parseFloat(values.depositGtWithdrawAmount)
						: null,
				presetId: values.presetId || null,
				presetNumber: values.presetNumber
					? parseInt(values.presetNumber)
					: null,
			}

			await updateTag({ id: tag.id, payload })

			toast.success('แก้ไขแท็กสำเร็จ')

			// Invalidate queries to refresh data
			queryClient.invalidateQueries({
				queryKey: [TagsQueryKeys.GetTags],
			})
			queryClient.invalidateQueries({
				queryKey: [TagsQueryKeys.GetTagById, tagId],
			})
			queryClient.invalidateQueries({
				queryKey: [TagsQueryKeys.GetTagStatistics],
			})
			queryClient.invalidateQueries({
				queryKey: [PlayerQueryKeys.GetPlayerList],
			})

			onOpenChange(false)
		} catch (error) {
			showErrorToast(error as Error)
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>แก้ไขแท็ก</DialogTitle>
					<DialogDescription>แก้ไขข้อมูลแท็กผู้เล่น</DialogDescription>
				</DialogHeader>

				{isLoadingTag ? (
					<div className="flex items-center justify-center py-8">
						<div className="text-sm text-muted-foreground">
							กำลังโหลดข้อมูล...
						</div>
					</div>
				) : !tag ? (
					<div className="flex items-center justify-center py-8">
						<div className="text-sm text-destructive">ไม่พบข้อมูลแท็ก</div>
					</div>
				) : (
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel>ชื่อแท็ก</FormLabel>
										<FormControl>
											<Input placeholder="เช่น VIP 50K+" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="color"
								render={({ field }) => (
									<FormItem>
										<FormLabel>สีแท็ก</FormLabel>
										<div className="flex gap-2">
											<FormControl>
												<Input type="color" className="h-10 w-20" {...field} />
											</FormControl>
											<FormControl>
												<Input placeholder="#FFD700" {...field} />
											</FormControl>
										</div>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="autoTagCondition"
								render={({ field }) => (
									<FormItem>
										<FormLabel>เงื่อนไขแท็กอัตโนมัติ</FormLabel>
										<Select
											onValueChange={field.onChange}
											defaultValue={field.value}
										>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder="เลือกเงื่อนไข" />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												<SelectItem value="none">
													ไม่ใช้แท็กอัตโนมัติ
												</SelectItem>
												<SelectItem value="minDepositAmount">
													ยอดฝากขั้นต่ำ
												</SelectItem>
												<SelectItem value="depositGtWithdraw">
													ยอดฝาก {'>'} ยอดถอน
												</SelectItem>
											</SelectContent>
										</Select>
										<FormDescription>
											เลือกเงื่อนไขสำหรับแท็กอัตโนมัติ
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>

							{autoTagCondition === 'minDepositAmount' && (
								<FormField
									control={form.control}
									name="minDepositAmount"
									render={({ field }) => (
										<FormItem>
											<FormLabel>ยอดฝากขั้นต่ำ (บาท)</FormLabel>
											<FormControl>
												<Input
													type="number"
													placeholder="เช่น 50000"
													{...field}
												/>
											</FormControl>
											<FormDescription>
												ผู้เล่นที่มียอดฝากรวมมากกว่าหรือเท่ากับจำนวนนี้จะได้รับแท็กอัตโนมัติ
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>
							)}

							{autoTagCondition === 'depositGtWithdraw' && (
								<div className="rounded-md border bg-muted/50 p-4">
									<p className="text-sm text-muted-foreground">
										ผู้เล่นที่มียอดฝากรวมมากกว่ายอดถอนรวมจะได้รับแท็กอัตโนมัติ
										และจะถูกลบแท็กเมื่อถอนมากกว่าหรือเท่ากับฝาก
									</p>
								</div>
							)}

							<div className="space-y-4 rounded-lg border p-4">
								<div className="flex items-center gap-2">
									<div className="text-sm font-medium">การตั้งค่า RTP</div>
									<RtpPresetPrecedenceTooltip highlightLevel={4} />
								</div>
								<p className="text-xs text-muted-foreground">
									ตั้งค่า RTP Preset สำหรับผู้เล่นที่มีแท็กนี้
								</p>
								<PresetPriorityHint where="tag" className="w-full" />

								<FormField
									control={form.control}
									name="presetId"
									render={({ field }) => (
										<FormItem>
											<FormLabel>RTP Preset ID</FormLabel>
											<FormDescription>
												สำหรับ PGHARD และ JOKERHARD
											</FormDescription>
											<FormControl>
												<Select
													value={field.value || undefined}
													onValueChange={field.onChange}
												>
													<SelectTrigger>
														<SelectValue placeholder="เลือก RTP Preset" />
													</SelectTrigger>
													<SelectContent>
														<SelectItem value="">ไม่ใช้ RTP Preset</SelectItem>
														{presets?.presets.map((preset) => (
															<SelectItem key={preset.id} value={preset.id}>
																{preset.note}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="presetNumber"
									render={({ field }) => (
										<FormItem>
											<FormLabel>RTP Preset Number (เปอร์เซ็นต์ RTP)</FormLabel>
											<FormDescription>
												สำหรับ Provider อื่นๆ (ค่า 30-99)
											</FormDescription>
											<FormControl>
												<Input
													type="number"
													min={30}
													max={99}
													placeholder="เช่น 95"
													{...field}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<DialogFooter>
								<Button
									type="button"
									variant="outline"
									onClick={() => onOpenChange(false)}
									disabled={isLoading}
								>
									ยกเลิก
								</Button>
								<Button type="submit" disabled={isLoading || isLoadingTag}>
									{isLoading ? 'กำลังบันทึก...' : 'บันทึก'}
								</Button>
							</DialogFooter>
						</form>
					</Form>
				)}
			</DialogContent>
		</Dialog>
	)
}
